/**
 * 認証状態管理フック
 *
 * Firebase Authentication の状態を管理し、ユーザー情報を提供します。
 */

import { useState, useEffect, useCallback } from "react";
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  IdTokenResult,
} from "firebase/auth";
import { getAuthInstance, isUsingEmulator } from "../lib/firebase";
import { logLogin } from "../services/operationLogService";

/**
 * ユーザーロール
 */
export type UserRole = "super_admin" | "admin" | null;

/**
 * 認証状態
 */
interface AuthState {
  user: User | null;
  role: UserRole;
  loading: boolean;
  error: string | null;
}

/**
 * 認証フックの戻り値
 */
interface UseAuthReturn extends AuthState {
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isEmulator: boolean;
}

/**
 * 認証状態管理フック
 */
export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    role: null,
    loading: true,
    error: null,
  });

  // ロールを取得（トークンを強制リフレッシュ）
  const fetchRole = useCallback(async (user: User): Promise<UserRole> => {
    try {
      // true を指定してトークンを強制リフレッシュ（Custom Claims を最新化）
      const tokenResult: IdTokenResult = await user.getIdTokenResult(true);
      return (tokenResult.claims.role as UserRole) || null;
    } catch {
      return null;
    }
  }, []);

  // 認証状態の監視
  useEffect(() => {
    const auth = getAuthInstance();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const role = await fetchRole(user);
        setState({ user, role, loading: false, error: null });
      } else {
        setState({ user: null, role: null, loading: false, error: null });
      }
    });

    return () => unsubscribe();
  }, [fetchRole]);

  // ログイン
  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const auth = getAuthInstance();
      const credential = await signInWithEmailAndPassword(auth, email, password);

      // ロールを取得
      const role = await fetchRole(credential.user);

      // ロールがない場合はエラー
      if (!role) {
        await firebaseSignOut(auth);
        setState({
          user: null,
          role: null,
          loading: false,
          error: "管理者として登録されていません",
        });
        return false;
      }

      // ログインログを記録
      void logLogin();

      setState({ user: credential.user, role, loading: false, error: null });
      return true;
    } catch (error: unknown) {
      let errorMessage = "ログインに失敗しました";

      if (error instanceof Error && "code" in error) {
        const firebaseError = error as { code: string };
        switch (firebaseError.code) {
          case "auth/user-not-found":
          case "auth/wrong-password":
          case "auth/invalid-credential":
            errorMessage = "メールアドレスまたはパスワードが正しくありません";
            break;
          case "auth/too-many-requests":
            errorMessage = "ログイン試行回数が多すぎます。しばらく待ってからお試しください";
            break;
        }
      }

      setState({ user: null, role: null, loading: false, error: errorMessage });
      return false;
    }
  }, [fetchRole]);

  // ログアウト
  const signOut = useCallback(async () => {
    const auth = getAuthInstance();
    await firebaseSignOut(auth);
    setState({ user: null, role: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    signIn,
    signOut,
    isAdmin: state.role === "admin" || state.role === "super_admin",
    isEmulator: isUsingEmulator(),
  };
}
